import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, FileText, Download, BarChart3, TrendingUp,
  Home, CalendarDays, Users, Wrench, AlertTriangle, Euro,
  ChevronDown, ChevronUp,
} from "lucide-react";
import {
  getProprietaires, getRapports, getRapportMensuel,
  generateRapportMensuelPdf, downloadRapport,
} from "@/lib/api";

const MOIS_NOMS = [
  "", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function KpiCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col items-center gap-1" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className={`w-5 h-5 ${accent ? "text-[#C9922A]" : "text-[#004333]"}`} />
      <span className={`text-2xl font-bold font-mono ${accent ? "text-[#C9922A]" : "text-[#004333]"}`}>{value}</span>
      <span className="text-xs text-gray-500 uppercase tracking-wider text-center">{label}</span>
    </div>
  );
}

function BienRow({ bien, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const s = bien.synthese_financiere;
  const o = bien.occupation;
  const r = bien.revenus;
  const p = bien.prestations;

  return (
    <>
      <tr
        className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer transition-colors"
        onClick={() => setOpen(!open)}
        data-testid={`bien-row-${bien.logement_id}`}
      >
        <td className="p-3">
          <div className="flex items-center gap-2">
            {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            <div>
              <span className="font-semibold text-[#004333]">{bien.titre}</span>
              <span className="text-xs text-gray-400 ml-2">{bien.ville} · {bien.type_logement}</span>
            </div>
          </div>
        </td>
        <td className="p-3 text-center">{r.nb_reservations}</td>
        <td className="p-3 text-center">
          <span className="font-mono">{o.nuits_occupees}</span>
          <span className="text-gray-400 text-xs">/{o.nuits_disponibles}</span>
        </td>
        <td className="p-3 text-center">
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${o.taux_occupation >= 70 ? "bg-green-100 text-green-700" : o.taux_occupation >= 40 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
            {o.taux_occupation}%
          </span>
        </td>
        <td className="p-3 text-right font-mono">{s.revenus_bruts.toFixed(2)} EUR</td>
        <td className="p-3 text-right font-mono font-bold text-[#004333]">{s.revenu_net.toFixed(2)} EUR</td>
      </tr>
      {open && (
        <tr className="bg-gray-50/60" data-testid={`bien-detail-${bien.logement_id}`}>
          <td colSpan={6} className="p-4">
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              {/* Financier */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Euro className="w-3 h-3" /> Synthèse financière
                </h5>
                <div className="space-y-1.5">
                  <div className="flex justify-between"><span className="text-gray-600">Revenus bruts</span><span className="font-mono">{s.revenus_bruts.toFixed(2)} EUR</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Commission ({s.taux_commission}%)</span><span className="font-mono text-red-600">- {s.commission.toFixed(2)} EUR</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Prestations</span><span className="font-mono text-red-600">- {s.cout_prestations.toFixed(2)} EUR</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Incidents</span><span className="font-mono text-red-600">- {s.cout_incidents.toFixed(2)} EUR</span></div>
                  <div className="flex justify-between border-t pt-1.5 mt-1.5 border-gray-200">
                    <span className="font-semibold text-[#004333]">Revenu net</span>
                    <span className="font-mono font-bold text-[#004333]">{s.revenu_net.toFixed(2)} EUR</span>
                  </div>
                </div>
              </div>

              {/* Occupation */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" /> Occupation
                </h5>
                <div className="space-y-1.5">
                  <div className="flex justify-between"><span className="text-gray-600">Nuits occupées</span><span className="font-mono">{o.nuits_occupees}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Nuits disponibles</span><span className="font-mono">{o.nuits_disponibles}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Taux d'occupation</span><span className="font-mono font-bold">{o.taux_occupation}%</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Prix moyen / nuit</span><span className="font-mono">{r.prix_moyen_nuit.toFixed(2)} EUR</span></div>
                </div>
                <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#004333] h-2 rounded-full transition-all" style={{ width: `${Math.min(o.taux_occupation, 100)}%` }} />
                </div>
              </div>

              {/* Prestations */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Wrench className="w-3 h-3" /> Prestations ({p.total})
                </h5>
                {p.par_type.length > 0 ? (
                  <div className="space-y-1.5">
                    {p.par_type.map((pt, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-gray-600">{pt.label} (x{pt.count})</span>
                        <span className="font-mono">{pt.cout_total.toFixed(2)} EUR</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t pt-1.5 mt-1.5 border-gray-200">
                      <span className="font-semibold">Total</span>
                      <span className="font-mono font-bold">{p.cout_total.toFixed(2)} EUR</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400 text-xs">Aucune prestation ce mois-ci</p>
                )}
                {bien.incidents.total > 0 && (
                  <div className="mt-3 pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-1 text-red-600 text-xs font-semibold">
                      <AlertTriangle className="w-3 h-3" />
                      {bien.incidents.total} incident(s) — {bien.incidents.cout_total.toFixed(2)} EUR
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function RapportsPage() {
  const now = new Date();
  const [proprietaires, setProprietaires] = useState([]);
  const [logements, setLogements] = useState([]);
  const [rapports, setRapports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedProp, setSelectedProp] = useState("");
  const [selectedLogement, setSelectedLogement] = useState("");
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    loadInitial();
  }, []);

  const loadInitial = async () => {
    try {
      const [propRes, rapRes] = await Promise.all([
        getProprietaires(),
        getRapports(),
      ]);
      setProprietaires(propRes.data);
      setRapports(rapRes.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const res = await getRapportMensuel(mois, annee, selectedProp || undefined, selectedLogement || undefined);
      setPreview(res.data);
      // Extract logements from response for filter
      if (res.data.biens) {
        const uniqueLogements = res.data.biens.map(b => ({ id: b.logement_id, titre: b.titre, ville: b.ville }));
        setLogements(uniqueLogements);
      }
    } catch {
      toast.error("Erreur lors du chargement du rapport");
    } finally {
      setLoadingPreview(false);
    }
  }, [mois, annee, selectedProp, selectedLogement]);

  useEffect(() => {
    if (!loading) loadPreview();
  }, [mois, annee, selectedProp, selectedLogement, loading, loadPreview]);

  const handleGeneratePdf = async () => {
    setGenerating(true);
    try {
      const res = await generateRapportMensuelPdf(mois, annee, selectedProp || undefined);
      toast.success("Rapport PDF généré !");
      window.open(downloadRapport(res.data.fichier_pdf), "_blank");
      const rapRes = await getRapports();
      setRapports(rapRes.data);
    } catch {
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setGenerating(false);
    }
  };

  const getPropName = (propId) => {
    if (propId === "global") return "Global";
    return proprietaires.find((p) => p.id === propId)?.nom || "Inconnu";
  };

  const syn = preview?.synthese || {};
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="rapports-page">
      <div className="mb-6">
        <h2 className="text-2xl font-display text-[#004333]">Rapports mensuels</h2>
        <p className="text-gray-500">Revenus, occupation, prestations et incidents — par bien et par propriétaire</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-4 mb-6 rounded-lg" data-testid="rapport-filters">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Mois</label>
            <select value={mois} onChange={(e) => setMois(Number(e.target.value))} className="input-hh" data-testid="rapport-mois">
              {MOIS_NOMS.slice(1).map((n, i) => (
                <option key={i + 1} value={i + 1}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Année</label>
            <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))} className="input-hh" data-testid="rapport-annee">
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Propriétaire</label>
            <select value={selectedProp} onChange={(e) => { setSelectedProp(e.target.value); setSelectedLogement(""); }} className="input-hh" data-testid="rapport-proprietaire">
              <option value="">Tous</option>
              {proprietaires.map((p) => (
                <option key={p.id} value={p.id}>{p.nom}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Bien</label>
            <select value={selectedLogement} onChange={(e) => setSelectedLogement(e.target.value)} className="input-hh" data-testid="rapport-logement">
              <option value="">Tous</option>
              {logements.map((l) => (
                <option key={l.id} value={l.id}>{l.titre} — {l.ville}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* KPIs */}
      {loadingPreview ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-[#004333]" />
        </div>
      ) : preview ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="rapport-kpis">
            <KpiCard icon={Home} label="Biens" value={syn.nb_biens || 0} />
            <KpiCard icon={BarChart3} label="Réservations" value={syn.nb_reservations || 0} />
            <KpiCard icon={CalendarDays} label="Nuits occupées" value={syn.nuits_occupees || 0} />
            <KpiCard icon={TrendingUp} label="Taux occup. moy." value={`${syn.taux_occupation_moyen || 0}%`} />
            <KpiCard icon={Euro} label="Revenus bruts" value={`${(syn.revenus_bruts || 0).toFixed(0)} EUR`} />
            <KpiCard icon={Euro} label="Revenu net" value={`${(syn.revenu_net_total || 0).toFixed(0)} EUR`} accent />
          </div>

          {/* Per-proprietaire summary */}
          {preview.proprietaires && preview.proprietaires.length > 0 && !selectedLogement && (
            <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden" data-testid="rapport-proprietaires">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-display text-lg text-[#004333] flex items-center gap-2">
                  <Users className="w-5 h-5" /> Synthèse par propriétaire
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left p-3 font-semibold text-[#004333]">Propriétaire</th>
                      <th className="text-center p-3 font-semibold text-[#004333]">Biens</th>
                      <th className="text-center p-3 font-semibold text-[#004333]">Résa.</th>
                      <th className="text-center p-3 font-semibold text-[#004333]">Occup.</th>
                      <th className="text-right p-3 font-semibold text-[#004333]">Rev. bruts</th>
                      <th className="text-right p-3 font-semibold text-[#004333]">Rev. net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.proprietaires.map((p) => (
                      <tr key={p.proprietaire_id} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="p-3 font-semibold text-[#004333]">{p.proprietaire_nom}</td>
                        <td className="p-3 text-center">{p.nb_biens}</td>
                        <td className="p-3 text-center">{p.nb_reservations}</td>
                        <td className="p-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${p.taux_occupation >= 70 ? "bg-green-100 text-green-700" : p.taux_occupation >= 40 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                            {p.taux_occupation}%
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono">{p.revenus_bruts.toFixed(2)} EUR</td>
                        <td className="p-3 text-right font-mono font-bold text-[#004333]">{p.revenu_net.toFixed(2)} EUR</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Per-bien detail */}
          {preview.biens && preview.biens.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden" data-testid="rapport-biens">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-display text-lg text-[#004333] flex items-center gap-2">
                  <Home className="w-5 h-5" /> Détail par bien
                </h3>
                <button
                  onClick={handleGeneratePdf}
                  disabled={generating}
                  className="btn-primary flex items-center gap-2 text-sm"
                  data-testid="generate-pdf-btn"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {generating ? "Génération..." : "Exporter PDF"}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left p-3 font-semibold text-[#004333]">Logement</th>
                      <th className="text-center p-3 font-semibold text-[#004333]">Résa.</th>
                      <th className="text-center p-3 font-semibold text-[#004333]">Nuits</th>
                      <th className="text-center p-3 font-semibold text-[#004333]">Occup.</th>
                      <th className="text-right p-3 font-semibold text-[#004333]">Rev. bruts</th>
                      <th className="text-right p-3 font-semibold text-[#004333]">Rev. net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.biens.map((b, i) => (
                      <BienRow key={b.logement_id} bien={b} defaultOpen={i === 0 && preview.biens.length <= 3} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.biens && preview.biens.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center mb-6">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">Aucune donnée pour {MOIS_NOMS[mois]} {annee}.</p>
              <p className="text-gray-400 text-sm mt-1">Essayez un autre mois ou vérifiez vos réservations.</p>
            </div>
          )}
        </>
      ) : null}

      {/* Previous rapports */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="rapports-historique">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-display text-lg text-[#004333] flex items-center gap-2">
            <FileText className="w-5 h-5" /> Historique des rapports PDF
          </h3>
        </div>
        {rapports.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left p-3 font-semibold text-[#004333]">Propriétaire</th>
                <th className="text-left p-3 font-semibold text-[#004333]">Période</th>
                <th className="text-left p-3 font-semibold text-[#004333]">Généré le</th>
                <th className="text-right p-3 font-semibold text-[#004333]">Action</th>
              </tr>
            </thead>
            <tbody>
              {rapports.map((rapport) => (
                <tr key={rapport.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-3 font-medium text-[#004333]">{getPropName(rapport.proprietaire_id)}</td>
                  <td className="p-3 text-gray-600">{rapport.periode_debut} - {rapport.periode_fin}</td>
                  <td className="p-3 text-gray-600">{new Date(rapport.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-3 text-right">
                    <a href={downloadRapport(rapport.fichier_pdf)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#C9922A] hover:underline text-xs font-semibold">
                      <Download className="w-3.5 h-3.5" /> Télécharger
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun rapport PDF généré pour le moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}
